import { Box, Theme } from '@mui/material';
import { CSSObject } from 'tss-react';
import Dot from './Dot';
import Typography, { TypographyProps } from './v2/ui/Typography/Typography';

export type LegendVariant = 'default' | 'tag';

const TAG_LINE_HEIGHT_IN_PIXELS = 16;

const legendBaseStyles: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'nowrap',
};

const getLegendVariantStyles = (theme: Theme, variant: LegendVariant): CSSObject => {
  if (variant === 'tag') {
    return {
      display: 'inline-flex',
      gap: theme.spacing(0.75),
      maxWidth: '100%',
      paddingInline: theme.spacing(0.75),
      borderRadius: `${theme.shape.borderRadiusFull}px`,
      boxShadow: `inset 0 0 0 0.5px ${theme.palette.custom.border.primary}`,
    };
  }

  return { gap: theme.spacing(1) };
};

const DOT_SIZE_BY_VARIANT: Record<LegendVariant, 'small' | 'extraSmall'> = {
  default: 'small',
  tag: 'extraSmall',
};

const TYPOGRAPHY_PROPS_BY_VARIANT: Record<LegendVariant, TypographyProps> = {
  default: {},
  tag: {
    variant: 'body2',
    color: 'primary.main',
    sx: { lineHeight: `${TAG_LINE_HEIGHT_IN_PIXELS}px` },
  },
};

type LegendProps = {
  color: 'primary' | 'secondary' | 'tertiary' | 'disabled' | string;
  description: string;
  variant?: LegendVariant;
  typographyProps?: TypographyProps;
};

const Legend = ({ color, description, variant = 'default', typographyProps }: LegendProps) => (
  <Box sx={[legendBaseStyles, (theme) => getLegendVariantStyles(theme, variant)]}>
    <Dot color={color} size={DOT_SIZE_BY_VARIANT[variant]} />

    <Typography noWrap {...TYPOGRAPHY_PROPS_BY_VARIANT[variant]} {...typographyProps}>
      {description}
    </Typography>
  </Box>
);

export default Legend;
